import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { useSEO } from '@/hooks/useSEO';

export default function CmsConditionsOfParticipation() {
  useSEO({
    title: 'CMS Conditions of Participation for Home Health Agencies | Advantixx',
    description: 'A comprehensive breakdown of CMS Conditions of Participation (CoPs) for home health agencies — what they are, why they matter, and how ACHC accreditation satisfies them.',
    keywords: 'CMS conditions of participation, home health CoPs, 42 CFR Part 484, ACHC deeming authority, Medicare home health compliance',
    canonical: '/blog/home-health-cms-conditions-of-participation',
    ogType: 'article',
    articlePublishedTime: '2026-01-20',
  });

  useEffect(() => {
    window.scrollTo(0, 0);
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'schema-cms-cops';
    script.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'Article',
      '@id': `${siteUrl}/blog/home-health-cms-conditions-of-participation#article`,
      url: `${siteUrl}/blog/home-health-cms-conditions-of-participation`,
      headline: 'Understanding CMS Conditions of Participation for Home Health Agencies',
      description: 'A comprehensive breakdown of CMS Conditions of Participation (CoPs) for home health agencies — what they are, why they matter, and how ACHC accreditation satisfies them.',
      image: 'https://public.readdy.ai/ai/img_res/d11c81c7e49746ab75730562d4f49135.jpg',
      datePublished: '2026-01-20',
      dateModified: '2026-01-20',
      author: {
        '@type': 'Person',
        name: 'Dr. Osmel Villarreal',
        jobTitle: 'ACHC Certified Consultant, DBA, MBA, MS',
        url: `${siteUrl}/why-dr-villarreal`,
      },
      publisher: {
        '@type': 'Organization',
        name: 'Advantixx Home Health Accreditation Consultants',
        url: siteUrl,
      },
      isPartOf: { '@id': `${siteUrl}/blog#webpage` },
      keywords: ['CMS conditions of participation', 'home health CoPs', '42 CFR 484', 'ACHC deeming authority', 'Medicare compliance'],
      articleSection: 'Compliance',
    });
    document.head.appendChild(script);
    return () => { document.getElementById('schema-cms-cops')?.remove(); };
  }, []);

  const cops = [
    { code: '484.40', title: 'Condition of Participation: Skilled Nursing Services', desc: 'Requires that skilled nursing services be provided by or under the supervision of a registered nurse, with specific documentation and care planning requirements.' },
    { code: '484.45', title: 'Condition of Participation: Therapy Services', desc: 'Covers physical, occupational, and speech-language pathology services — including qualifications, supervision, and documentation standards.' },
    { code: '484.50', title: 'Condition of Participation: Medical Social Services', desc: 'Addresses the provision of social services to patients and families, including assessment, counseling, and community resource coordination.' },
    { code: '484.55', title: 'Condition of Participation: Home Health Aide Services', desc: 'Establishes training, competency evaluation, and supervision requirements for home health aides providing personal care services.' },
    { code: '484.60', title: 'Condition of Participation: Care Planning, Coordination, and Quality of Care', desc: 'Requires comprehensive care plans developed by the interdisciplinary team, with ongoing coordination and quality monitoring.' },
    { code: '484.65', title: 'Condition of Participation: Quality Assessment and Performance Improvement', desc: 'Mandates a formal QAPI program that monitors outcomes, identifies problems, and implements measurable improvements.' },
    { code: '484.70', title: 'Condition of Participation: Infection Prevention and Control', desc: 'Requires a comprehensive infection prevention and control program, including surveillance, education, and outbreak management.' },
    { code: '484.75', title: 'Condition of Participation: Emergency Preparedness', desc: 'Mandates an emergency preparedness program with risk assessment, policies, communication plans, and annual testing.' },
  ];

  return (
    <div className="min-h-screen bg-[#0d0d0d] font-['Montserrat',sans-serif]">
      <Navbar />
      <section className="relative pt-36 pb-0">
        <div className="relative h-[420px] md:h-[500px] overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=healthcare%20compliance%20professional%20studying%20federal%20regulations%20and%20policy%20documents%20at%20a%20clean%20modern%20desk%2C%20professional%20medical%20office%20setting%2C%20warm%20ambient%20lighting%2C%20gold%20accents%2C%20high%20quality&width=1400&height=600&seq=cmscops1&orientation=landscape"
            alt="CMS Conditions of Participation for Home Health Agencies"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0d0d0d] via-[#0d0d0d]/50 to-[#0d0d0d]/20"></div>
          <div className="absolute bottom-0 left-0 right-0 max-w-4xl mx-auto px-6 md:px-10 pb-12">
            <div className="flex flex-wrap items-center gap-3 mb-4">
              <span className="bg-[#d4af37] text-[#0d0d0d] text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wide">Compliance</span>
              <span className="text-[#888] text-xs">January 20, 2026</span>
              <span className="text-[#888] text-xs">•</span>
              <span className="text-[#888] text-xs">12 min read</span>
            </div>
            <h1 className="text-3xl md:text-4xl font-black text-white leading-tight">
              Understanding CMS Conditions of Participation<br className="hidden md:block" />
              <span className="text-[#d4af37]"> for Home Health Agencies</span>
            </h1>
          </div>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-6 md:px-10 py-16">
        <div className="flex flex-col lg:flex-row gap-12">
          <article className="flex-1 min-w-0">
            <p className="text-[#bbb] text-lg leading-relaxed mb-8 border-l-4 border-[#d4af37] pl-6">
              CMS Conditions of Participation (CoPs) are the federal standards that home health agencies must meet to participate in Medicare and Medicaid programs. Understanding these regulations is the foundation of every successful compliance and accreditation strategy.
            </p>

            <h2 className="text-2xl font-black text-white mb-5 mt-12"><span className="text-[#d4af37]">01.</span> What Are CMS CoPs?</h2>
            <p className="text-[#999] leading-relaxed mb-6">
              The Centers for Medicare &amp; Medicaid Services (CMS) establishes Conditions of Participation as the minimum health and safety standards that healthcare providers must meet to receive Medicare and Medicaid reimbursement. For home health agencies, these CoPs are codified in 42 CFR Part 484.
            </p>
            <p className="text-[#999] leading-relaxed mb-8">
              The CoPs were significantly revised in 2017 and became effective January 13, 2018 — representing the most comprehensive update in decades. The revised CoPs shifted focus from process compliance to patient-centered outcomes and quality improvement.
            </p>

            <h2 className="text-2xl font-black text-white mb-5 mt-12"><span className="text-[#d4af37]">02.</span> How ACHC Accreditation Satisfies CoPs</h2>
            <p className="text-[#999] leading-relaxed mb-6">
              ACHC holds CMS deeming authority for home health agencies. This means that when your agency achieves ACHC accreditation, CMS deems you to have met the Medicare Conditions of Participation — eliminating the need for a separate state survey.
            </p>
            <div className="bg-[#111] border border-[#d4af37]/20 rounded-xl p-6 mb-10">
              <p className="text-[#d4af37] text-xs uppercase tracking-widest font-bold mb-4">Key Benefits of Deeming Authority</p>
              <ul className="flex flex-col gap-3">
                {['No separate CMS state survey required', 'Streamlined Medicare certification process', 'Reduced regulatory burden and survey frequency', 'Single accreditation satisfies both state and federal requirements', 'Ongoing compliance support from ACHC account advisors'].map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-[#888]">
                    <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5"><i className="ri-checkbox-circle-fill text-[#d4af37] text-sm"></i></div>
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            <h2 className="text-2xl font-black text-white mb-6 mt-12"><span className="text-[#d4af37]">03.</span> Key CoPs Every Agency Must Know</h2>
            <div className="flex flex-col gap-5 mb-10">
              {cops.map((cop, i) => (
                <div key={i} className="bg-[#111] border border-[#1e1e1e] rounded-xl p-6">
                  <div className="flex items-start gap-4">
                    <span className="bg-[#d4af37]/10 text-[#d4af37] text-xs font-bold px-3 py-1.5 rounded-md whitespace-nowrap flex-shrink-0">{cop.code}</span>
                    <div>
                      <h3 className="text-white font-bold text-sm mb-2">{cop.title}</h3>
                      <p className="text-[#777] text-sm leading-relaxed">{cop.desc}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <h2 className="text-2xl font-black text-white mb-5 mt-12"><span className="text-[#d4af37]">04.</span> Common CoP Deficiencies Found During Surveys</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-10">
              {['Incomplete or missing care plans', 'Inadequate QAPI program documentation', 'Insufficient home health aide supervision records', 'Missing or outdated emergency preparedness plans', 'Incomplete infection control policies', 'Lack of interdisciplinary team coordination documentation', 'Deficient patient rights notifications', 'Inadequate clinical record organization'].map((item, i) => (
                <div key={i} className="flex items-start gap-3 bg-[#111] rounded-lg p-4 border border-[#1e1e1e]">
                  <div className="w-5 h-5 flex items-center justify-center mt-0.5 flex-shrink-0"><i className="ri-close-circle-line text-red-400 text-sm"></i></div>
                  <span className="text-[#888] text-sm leading-relaxed">{item}</span>
                </div>
              ))}
            </div>

            <div className="bg-gradient-to-br from-[#d4af37]/10 to-[#d4af37]/5 border border-[#d4af37]/30 rounded-xl p-8 text-center mt-12">
              <h3 className="text-white font-black text-xl mb-3">Ensure Full CoP Compliance with Expert Guidance</h3>
              <p className="text-[#888] text-sm mb-6 leading-relaxed">Advantixx helps your agency achieve and maintain full CMS CoP compliance through ACHC accreditation.</p>
              <Link to="/services" className="inline-flex items-center gap-2 bg-[#d4af37] text-[#0d0d0d] font-bold text-sm px-8 py-3.5 rounded-full hover:bg-[#f4c430] transition-colors duration-200 whitespace-nowrap cursor-pointer">
                Book a Free Consultation <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </article>

          <aside className="lg:w-72 flex-shrink-0">
            <div className="sticky top-28 flex flex-col gap-6">
              <div className="bg-[#111] border border-[#1e1e1e] rounded-xl p-6">
                <p className="text-[#d4af37] text-xs uppercase tracking-widest font-semibold mb-4">About the Author</p>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-full bg-[#d4af37]/10 border border-[#d4af37]/30 flex items-center justify-center flex-shrink-0"><i className="ri-user-line text-[#d4af37] text-lg"></i></div>
                  <div><p className="text-white font-bold text-sm">Dr. Osmel Villarreal</p><p className="text-[#666] text-xs">DBA, MBA, MS</p></div>
                </div>
                <p className="text-[#777] text-xs leading-relaxed">ACHC Certified Consultant with doctoral expertise in home health compliance and federal regulatory standards.</p>
                <Link to="/why-dr-villarreal" className="mt-4 flex items-center gap-1.5 text-[#d4af37] text-xs font-semibold hover:gap-2.5 transition-all duration-200 whitespace-nowrap cursor-pointer">Learn More <i className="ri-arrow-right-line"></i></Link>
              </div>
              <div className="bg-[#111] border border-[#1e1e1e] rounded-xl p-6">
                <p className="text-[#d4af37] text-xs uppercase tracking-widest font-semibold mb-5">Related Articles</p>
                <div className="flex flex-col gap-5">
                  {[
                    { title: 'Complete Guide to ACHC Accreditation', path: '/blog/achc-accreditation-guide' },
                    { title: 'ACHC vs. The Joint Commission', path: '/blog/achc-vs-joint-commission' },
                    { title: 'Top 10 Compliance Tips for 2026', path: '/blog/home-health-compliance-tips' },
                  ].map((post, i) => (
                    <Link key={i} to={post.path} className="group cursor-pointer">
                      <p className="text-[#aaa] text-sm leading-snug group-hover:text-[#d4af37] transition-colors duration-200">{post.title}</p>
                      <span className="text-[#555] text-xs mt-1 flex items-center gap-1 group-hover:gap-2 transition-all duration-200">Read <i className="ri-arrow-right-line text-xs"></i></span>
                    </Link>
                  ))}
                </div>
              </div>
              <div className="bg-gradient-to-br from-[#d4af37]/15 to-[#d4af37]/5 border border-[#d4af37]/30 rounded-xl p-6 text-center">
                <i className="ri-calendar-check-line text-[#d4af37] text-3xl mb-3 block"></i>
                <p className="text-white font-bold text-sm mb-2">Free Consultation</p>
                <p className="text-[#777] text-xs mb-4 leading-relaxed">Get your personalized ACHC accreditation roadmap today.</p>
                <Link to="/services" className="block bg-[#d4af37] text-[#0d0d0d] font-bold text-xs px-5 py-2.5 rounded-full hover:bg-[#f4c430] transition-colors duration-200 whitespace-nowrap cursor-pointer">Book Now</Link>
              </div>
            </div>
          </aside>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-6 md:px-10 pb-16">
        <Link to="/blog" className="inline-flex items-center gap-2 text-[#888] hover:text-[#d4af37] text-sm transition-colors duration-200 cursor-pointer">
          <i className="ri-arrow-left-line"></i> Back to All Articles
        </Link>
      </div>
      <Footer />
    </div>
  );
}
